import {firstValueFrom} from 'rxjs';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {CpmCUserApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-user.api-model';
import {
    NewEditUserModalComponent,
    NewEditUserModalConfig,
    NewEditUserModalResult
} from '../../modals/new-edit-user-modal/new-edit-user-modal.component';
import {IBaseCRUModalResultBase} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {CpmCUserApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-user.api-service';
import {Component} from '@angular/core';
import {
    ETableColDataType,
    ETableColFilterType,
    TableTagDef
} from '../../../../sdk/src/components/generic-table/table-col-def';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {CpmCUserRoleApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-user-role.api-service';
import {CpmCUserRoleApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-user-role.api-model';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {CpmCUserApiUtils} from '../../../../gen/cpm-c-api-ts/src/utils/c-global/cpmc-user.api-utils';
import {ECpmCUserRoleTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {UserUtils} from '../../utils/user.utils';

/**
 * User CRUD table component
 */
@Component({
    selector: 'app-user-crud',
    templateUrl: './user-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class UserCRUDComponent extends IBaseCRUDComponent<
    CpmCUserApiModel,
    NewEditUserModalComponent,
    NewEditUserModalConfig,
    NewEditUserModalResult
> {
    private globalUserRoles: CpmCUserRoleApiModel[] = [];

    // ===============================================================
    // ===============================================================

    protected helpVisible = false;
    protected userTypeDescriptions = UserUtils.typeDescriptions;
    protected userTypeTags: TableTagDef[] = UserUtils.getTypeTableTagDefs(this.generateColorFromString.bind(this));
    protected globalUserRoleTags: TableTagDef[] = [];

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'UserApiModel',
            getModelHumanIdentifier: (model: CpmCUserApiModel) => model.email ?? '<Unknown user>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCUserApiModel) => CpmCUserApiService.deleteUser(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditUserModalComponent,
            getDialogServiceParameters: NewEditUserModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCUserApiModel) => new NewEditUserModalConfig(model),

            // Authorization config
            authorizationNew: CpmCUserApiService.endpointNew(),
            authorizationEdit: CpmCUserApiService.endpointEdit(this.FAKE_UUID),
            authorizationDelete: CpmCUserApiService.endpointDelete(this.FAKE_UUID)
        });

        this.onModalResult.subscribe((res: IBaseCRUModalResultBase<NewEditUserModalResult>) => {
            if (CpmCUserApiUtils.is_user_as_machine_to_machine_user(res.extended?.item) && res.extended?.secretKey) {
                this.globalsService.confirmationService.confirm({
                    header: 'Secret key',
                    message:
                        '<center>' +
                        'Your machine has been created successfully.<br/>' +
                        "Please save the secret key below. You won't be able to see it again<br/>" +
                        '<br/><b>' +
                        res.extended.secretKey +
                        '</b><br/>' +
                        '</center>',
                    rejectVisible: false
                });
            }
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    public override async onRefresh(): Promise<void> {
        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading global user roles...',
                f: async (): Promise<void> => {
                    this.globalUserRoles = (
                        await firstValueFrom(
                            CpmCUserRoleApiService.getUserRoles(
                                {type: ECpmCUserRoleTypeApiEnum.GLOBAL},
                                CpmCUserRoleApiService.dPaginatorConfig
                            )
                        )
                    ).data;
                    this.globalUserRoleTags.splice(0, this.globalUserRoleTags.length);
                    this.globalUserRoleTags.push(
                        ...this.globalUserRoles.map((x) => ({
                            label: x.name,
                            value: x.uuid!,
                            style: {background: this.generateColorFromString(x.uuid + x.name, true)}
                        }))
                    );
                }
            },
            {
                m: 'Loading users...',
                f: async (): Promise<void> => {
                    const result = await firstValueFrom(CpmCUserApiService.getUserList({}, this.paginatorConfig));
                    this.items = result.data;
                    this.totalItemCount = result.total;
                }
            }
        ]);
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'type',
                header: 'Type',
                dataType: ETableColDataType.TAGS,
                searcheable: false,
                filterable: true,
                filterType: ETableColFilterType.SELECT,
                filterSelectOptions: this.userTypeTags,
                tags: this.userTypeTags
            },
            {
                field: 'firstname',
                header: 'Firstname',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'lastname',
                header: 'Lastname',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'email',
                header: 'Email',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'global_user_role_uuid',
                header: 'Global User Role',
                dataType: ETableColDataType.TAGS,
                searcheable: false,
                filterable: true,
                filterType: ETableColFilterType.SELECT,
                filterSelectOptions: this.globalUserRoleTags,
                tags: this.globalUserRoleTags
            },
            {
                field: 'expiration_date',
                header: 'Is expired',
                searcheable: false,
                filterable: false,
                dataType: ETableColDataType.BOOLEAN_LABEL,
                fnCustomData(row: CpmCUserApiModel) {
                    return row.expiration_date ? new Date(row.expiration_date) < new Date() : false;
                }
            },
            {
                field: 'from_ad',
                header: 'Is External',
                searcheable: false,
                filterable: true,
                filterType: ETableColFilterType.BOOLEAN,
                dataType: ETableColDataType.BOOLEAN_ICON
            }
        ];

        this.addCreatedColumns();
    }
}
